import React from 'react';
import { InsightData } from '../../MoleculeSearch.type';
import InsightCard from './components/InsightCard/InsightCard';
import { InsightCardProps } from './components/InsightCard/InsightCard';
import './MarketInsightsSection.styles.scss';
import insightsIcon from 'assets/icons/insightsIcon.svg';

interface MarketInsightsSectionProps {
  insights: InsightData[] | null;
  loading?: boolean;
  error?: string | null;
}

/**
 * Skeleton loader for insight cards
 */
const InsightCardSkeleton: React.FC = () => (
  <div className="insight-card skeleton">
    <div className="insight-title skeleton-text" style={{ width: '60%', height: '1.2rem' }} />
    <div className="insight-value skeleton-text" style={{ width: '40%', height: '2rem', marginTop: '0.5rem' }} />
    <div className="insight-desc skeleton-text" style={{ width: '90%', height: '2.5rem', marginTop: '0.5rem' }} />
  </div>
);

/**
 * Error display component
 */
const ErrorDisplay: React.FC<{ message: string }> = ({ message }) => (
  <div className="market-insights-error">
    <i className="fas fa-exclamation-triangle" style={{ color: 'var(--error-color)', marginRight: '0.5rem' }} />
    <span>{message}</span>
  </div>
);

const MarketInsightsSection: React.FC<MarketInsightsSectionProps> = ({
  insights,
  loading = false,
  error = null,
}) => {
  // Determine what to render based on state
  const renderContent = () => {
    // Show skeleton loading state
    if (loading) {
      return (
        <div className="three-card-grid">
          {[...Array(9)].map((_, index) => (
            <div key={`skeleton-${index}`} className="three-card-wrapper">
              <InsightCardSkeleton />
            </div>
          ))}
        </div>
      );
    }

    // Show error state
    if (error) {
      return <ErrorDisplay message={error} />;
    }

    // Show insights data
    if (insights && insights.length > 0) {
      return (
        <div className="three-card-grid">
          {insights.map((insight, index) => (
            <div key={index} className="three-card-wrapper">
              <InsightCard {...(insight as InsightCardProps)} />
            </div>
          ))}
        </div>
      );
    }

    // No data available
    return (
      <div className="market-insights-empty">
        <i className="fas fa-chart-bar" style={{ fontSize: '2rem', color: 'var(--gray-400)', marginBottom: '0.5rem' }} />
        <p>No market insights data available</p>
      </div>
    );
  };

  return (
    <section id="market-insights" className="content-section">
      <div className="section-header">
        <div className="section-title">
          <img src={insightsIcon} alt="Insights" />
          Market Insights & Trends
        </div>
        <small style={{ color: 'var(--gray-500)', fontWeight: 500, fontSize: '1rem' }}>
          Real-time market intelligence and competitive analysis
        </small>
      </div>
      <div className="section-content">{renderContent()}</div>
    </section>
  );
};

export default MarketInsightsSection;
